import { Component, OnInit, AfterViewInit, ElementRef, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { gsap } from 'gsap';
import { OrdersUserService } from './service/orders-user.service';
import { Orders } from './interfaces/orders-user.interface';

import { LoadImagesService } from '../../core/service/load-image.service';
import { Router } from '@angular/router';
import { ArrowBackComponent } from "../../shared/components/arrow-back/arrow-back.component";

@Component({
  selector: 'app-orders-user',
  standalone: true,
  imports: [ArrowBackComponent],
  templateUrl: './orders-user.component.html',
  styleUrl: './orders-user.component.css'
})
export class OrdersUserComponent implements OnInit, AfterViewInit {

  orders: Orders[] = [];
  loading: boolean = true;
  error: string | null = null;
  readonly defaultImage = 'assets/imagen_default.png';
  private animated = false;

  constructor(
    private ordersUserService: OrdersUserService,
    private loadImageService: LoadImagesService,
    private router: Router,
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    this.loadOrders();
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const container: HTMLElement = this.el.nativeElement as HTMLElement;
    const grid: HTMLElement | null = container.querySelector('.grid');
    const cards: NodeListOf<HTMLElement> = container.querySelectorAll('[#orderCard], div[\#orderCard]');

    // Estado inicial
    gsap.set(cards, { opacity: 0, y: 30 });

    // Animación al entrar en viewport
    if (grid) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.animated) {
            gsap.to(cards, {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: 'power2.out',
              stagger: 0.12
            });
            this.animated = true;
            observer.disconnect();
          }
        });
      }, { threshold: 0.15 });
      observer.observe(grid);
    }
  }

  // Hover sobre tarjeta
  onCardEnter(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const card = event.currentTarget as HTMLElement;
    const arrow = card.querySelector('.js-arrow-ico');
    gsap.to(card, {
      scale: 1.02,
      y: -3,
      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
      duration: 0.25,
      ease: 'power2.out'
    });
    if (arrow) {
      gsap.to(arrow, {
        x: 4,
        duration: 0.25,
        ease: 'power2.out',
        repeat: 1,
        yoyo: true
      });
    }
  }

  onCardLeave(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const card = event.currentTarget as HTMLElement;
    gsap.to(card, {
      scale: 1,
      y: 0,
      boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
      duration: 0.25,
      ease: 'power2.out'
    });
  }

  // Hover sobre imagen
  onImageEnter(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const img = event.currentTarget as HTMLElement;
    gsap.to(img, {
      scale: 1.06,
      filter: 'brightness(1.05) contrast(1.02)',
      duration: 0.3,
      ease: 'power2.out'
    });
  }

  onImageLeave(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const img = event.currentTarget as HTMLElement;
    gsap.to(img, {
      scale: 1,
      filter: 'brightness(1) contrast(1)',
      duration: 0.3,
      ease: 'power2.out'
    });
  }

  loadOrders(): void {
    this.loading = true;
    this.ordersUserService.getOrders().subscribe({
      next: (resp) => {
        // Transforma el payment de cada orden (MKP: Tarjeta, Marketplace: Tarjeta, etc.)
        this.orders = resp.data.map(order => ({
          ...order,

          /* Transforma el código de payment a un nombre legible */
          payment: this.convertPaymentMethod(order.payment)
        }));
        this.loading = false;
      },
      error: (err) => {
        console.error('Error al cargar órdenes:', err);
        this.error = 'No se pudieron cargar las órdenes. Intente nuevamente.';
        this.loading = false;
      }
    });
  }

   // Método para convertir códigos de payment a nombres legibles
   convertPaymentMethod(code: string): string {
    const paymentMethods: { [key: string]: string } = {
      'Marketplace': 'Tarjeta',
      'MKP-Créditos': 'Tarjeta-Créditos',
      'MKP': 'Tarjeta',
    };

    // Si el código existe en el objeto, devuelve el nombre, sino devuelve el código original
    return paymentMethods[code] || code;
  }


  getImageUrl(imagePath: string | undefined): string {
    // If no image path is provided, return default image immediately
    if (!imagePath) {
      return this.defaultImage;
    }

    // Check if the image path is already a full URL or an asset path
    if (imagePath.startsWith('http') || imagePath.startsWith('assets/')) {
      return imagePath;
    }

    // Return the full path with the base URL
    return this.loadImageService.linkImage + imagePath;
  }
  onImageError(event: any): void {
    event.target.src = this.defaultImage;
  }
  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'entregado':
        return 'bg-green-500';
      case 'en proceso':
        return 'bg-blue-500';
      case 'cancelado':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  visitOrden(idOrden: number) {
    this.router.navigate([`auth/order/${idOrden}`])
  }
}
